import React, { useEffect, useState } from 'react';
import { Check, CircleHelp, X } from 'lucide-react';
import { api, AD_KIND_LABEL, type InterviewSession, type MarkAdmission } from '../api';
import { TYPE, useTheme } from '../theme';
import { Badge, Button, Card, Input, type BadgeTone } from '../components/ui';
import { isFreePurchase, kronor } from '../views/jakt/adPurchase';
import { InterviewCall } from '../admission/InterviewCall';
import { ContractSettings } from './ContractSettings';
import { admissionTone, reservationLeft } from '../admission/admissionText';

/**
 * P2 · Antagning per mark (ersätter P1:s lista "Ännu ej godkända"): jägare som köpt en annons,
 * deras profil mot markens krav, intervju, godkänn (kortet dras, avtalet skickas) eller avslå
 * (reservationen släpps). Därefter avtalets status (P3) tills jägaren ligger i gruppen.
 */
export function AdmissionsPanel({ markId, canEdit, isOwner }: { markId: string; canEdit: boolean; isOwner: boolean }) {
  const T = useTheme();
  const [list, setList] = useState<MarkAdmission[] | null>(null);
  const [err, setErr] = useState('');
  const [call, setCall] = useState<InterviewSession | null>(null);
  const [showDone, setShowDone] = useState(false);

  const load = () => api.markAdmissions(markId).then(setList).catch(e => { setErr(e.message); setList([]); });
  useEffect(() => { void load(); }, [markId]); // eslint-disable-line react-hooks/exhaustive-deps

  const replace = (a: MarkAdmission) => setList(l => (l ?? []).map(x => (x.id === a.id ? a : x)));

  if (!list) return null;
  const waiting = list.filter(a => a.stage === 'applied' || a.stage === 'interview');
  const inProgress = list.filter(a => a.stage === 'approved' || a.stage === 'contract_sent' || a.stage === 'signed');
  const done = list.filter(a => !waiting.includes(a) && !inProgress.includes(a));

  return (
    <section style={{ marginBottom: 28 }} data-testid="admissions-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <h2 style={{ ...TYPE.section, margin: 0, color: T.text }}>Antagning <span style={{ color: T.textMuted, fontWeight: 600 }}>({waiting.length})</span></h2>
        {waiting.length > 0 && <span style={{ ...TYPE.small, color: T.textMuted }}>Köp och ansökningar via markens annonser – inget dras förrän ni beslutat</span>}
      </div>
      {waiting.length === 0 && <div style={{ ...TYPE.small, color: T.textMuted, marginBottom: 12 }}>Inga jägare väntar på antagning.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {waiting.map(a => <AdmissionCard key={a.id} a={a} onChange={replace} onReload={load} onCall={setCall} />)}
      </div>
      {inProgress.length > 0 && <>
        <div style={{ ...TYPE.label, color: T.textMuted, margin: '20px 0 8px' }}>Godkända – avtal</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {inProgress.map(a => <AdmissionCard key={a.id} a={a} onChange={replace} onReload={load} onCall={setCall} />)}
        </div>
      </>}
      {done.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button onClick={() => setShowDone(v => !v)} style={{ background: 'none', border: 'none', color: T.accentText, fontWeight: 700, fontSize: 14, cursor: 'pointer', padding: '8px 0', minHeight: 44, fontFamily: 'inherit' }}>
            {showDone ? 'Dölj' : 'Visa'} avgjorda ({done.length})
          </button>
          {showDone && <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {done.map(a => <AdmissionCard key={a.id} a={a} onChange={replace} onReload={load} onCall={setCall} />)}
          </div>}
        </div>
      )}
      {err && <div role="alert" style={{ color: T.danger, marginTop: 8 }}>{err}</div>}
      <ContractSettings markId={markId} canEdit={canEdit} isOwner={isOwner} />
      {call && <InterviewCall session={call} onClose={() => setCall(null)} />}
    </section>
  );
}

const fmt = (ms: string | number | null | undefined, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }) =>
  ms ? new Date(Number(ms)).toLocaleString('sv-SE', opts) : '';

function AdmissionCard({ a, onChange, onReload, onCall }: { a: MarkAdmission; onChange: (a: MarkAdmission) => void; onReload: () => void; onCall: (s: InterviewSession) => void }) {
  const T = useTheme();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(a.stage === 'applied' || a.stage === 'interview');
  const [booking, setBooking] = useState(false);
  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');
  const p = a.profile;
  const deciding = a.stage === 'applied' || a.stage === 'interview';
  const left = deciding ? reservationLeft(a.releaseAt) : null;

  const run = async (key: string, fn: () => Promise<MarkAdmission | void>) => {
    setErr(''); setBusy(key);
    try { const r = await fn(); if (r) onChange(r); }
    catch (e: any) { setErr(e.message || 'Något gick fel'); onReload(); }
    finally { setBusy(''); }
  };
  const free = isFreePurchase(a);
  const approve = () => {
    const money = free ? 'Avtalet skickas med mejl.' : `${a.amount != null ? kronor(a.amount) : 'Beloppet'} dras från jägarens kort och avtalet skickas med mejl.`;
    if (!confirm(`Godkänna ${p?.name || a.userEmail}?\n\n${money}`)) return;
    void run('approve', () => api.approveAdmission(a.id));
  };
  const reject = () => {
    const n = prompt(`Avslå ${p?.name || a.userEmail}?${free ? '' : ' Reservationen släpps och inget dras.'}\n\nMeddelande till jägaren (valfritt):`, '');
    if (n === null) return;
    void run('reject', () => api.rejectAdmission(a.id, n));
  };
  const book = () => {
    const ms = Date.parse(when);
    if (!Number.isFinite(ms)) { setErr('Välj datum och tid'); return; }
    void run('book', async () => { const r = await api.bookAdmissionInterview(a.id, ms, note); setBooking(false); return r; });
  };
  const join = () => run('join', async () => { onCall(await api.joinAdmissionInterview(a.id)); });

  const tone: BadgeTone = admissionTone(a.stage);
  const btn: React.CSSProperties = { minHeight: 44 };

  return (
    <Card padding={0} data-testid="admission-card" data-stage={a.stage}>
      <div style={{ padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div aria-hidden style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', background: T.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: T.textSub }}>
          {p?.avatarUrl ? <img src={p.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (p?.name || a.userEmail).slice(0, 1).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, color: T.text }}>{p?.name || a.userName || a.userEmail}</span>
            <Badge tone={tone}>{a.stageLabel}</Badge>
            {deciding && a.meetsRequirements === false && <Badge tone="danger">Uppfyller inte alla krav</Badge>}
          </div>
          <div style={{ ...TYPE.small, color: T.textSub, marginTop: 2, overflowWrap: 'anywhere' }}>
            {a.adTitle || (a.adKind ? AD_KIND_LABEL[a.adKind] : 'Annons')}{a.dateStart ? ` · ${a.dateStart}${a.dateEnd && a.dateEnd !== a.dateStart ? ` – ${a.dateEnd}` : ''}` : ''}{a.amount != null ? ` · ${free ? 'Ansökan utan kort' : kronor(a.amount)}` : ''}{a.groupName ? ` · ${a.groupName}` : ''}
          </div>
          {left && (
            <div data-testid="reservation-deadline" style={{ ...TYPE.small, marginTop: 4, color: left.urgent ? T.danger : T.textMuted, fontWeight: left.urgent ? 700 : 500 }}>
              Beslut senast {fmt(a.releaseAt)} ({left.text} kvar) – {free ? 'annars går ansökan ut' : 'annars släpps kortreservationen'}
            </div>
          )}
          {a.interviewAt && deciding && <div style={{ ...TYPE.small, marginTop: 4, color: T.text }}>Intervju {fmt(a.interviewAt, { dateStyle: 'full', timeStyle: 'short' })}{a.interviewNote ? ` · ${a.interviewNote}` : ''}</div>}
          {a.stage === 'rejected' && a.decisionNote && <div style={{ ...TYPE.small, marginTop: 4, color: T.textMuted }}>”{a.decisionNote}”</div>}
        </div>
        {(p || a.requirements?.length) ? (
          <button aria-expanded={open} onClick={() => setOpen(v => !v)} style={{ background: 'none', border: 'none', color: T.accentText, fontWeight: 700, cursor: 'pointer', fontSize: 14, minHeight: 44, fontFamily: 'inherit', flexShrink: 0 }}>
            {open ? 'Dölj' : 'Profil'}
          </button>
        ) : null}
      </div>

      {open && (p || a.requirements) && (
        <div style={{ padding: '0 16px 14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {p && (
            <div style={{ ...TYPE.small, color: T.textSub, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ ...TYPE.label, color: T.textMuted, marginBottom: 2 }}>Profil</div>
              <div>{p.email}{p.region ? ` · ${p.region}` : ''}</div>
              <div>Jägarexamen: {p.hasExam ? `uppladdad${p.platformApproval === 'approved' ? ' (granskad av Qvenari)' : ''}` : 'saknas'}{p.hunterSinceYear ? ` · sedan ${p.hunterSinceYear}` : ''}</div>
              {p.experienceLevel && <div>Erfarenhet: {p.experienceLevel}</div>}
              <div>Vapenlicenser: {p.validWeaponLicenses}{p.weaponTypes.length ? ` · ${p.weaponTypes.join(', ')}` : ''}</div>
              <div>Hund: {p.dogs ? `${p.dogs} st` : p.dogHandler ? 'hundförare' : 'nej'} · Eftersök: {p.afterhuntCertified ? 'ja' : 'nej'}</div>
              {p.shootingTest && <div>Skjutprov: {p.shootingTest}</div>}
              {p.insurance && <div>Försäkring: {p.insurance}</div>}
              {p.bio && <div style={{ marginTop: 4, color: T.text, whiteSpace: 'pre-wrap' }}>{p.bio}</div>}
            </div>
          )}
          {a.requirements && a.requirements.length > 0 && (
            <div>
              <div style={{ ...TYPE.label, color: T.textMuted, marginBottom: 6 }}>Markens krav</div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="admission-requirements">
                {a.requirements.map(r => (
                  <li key={r.key} data-status={r.status} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', ...TYPE.small }}>
                    <span aria-label={r.status === 'ok' ? 'Uppfyllt' : r.status === 'missing' ? 'Saknas' : 'Bedöm själv'}
                      style={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: r.status === 'ok' ? T.okSoft : r.status === 'missing' ? T.dangerSoft : T.surface2, color: r.status === 'ok' ? T.ok : r.status === 'missing' ? T.danger : T.textSub }}>
                      {r.status === 'ok' ? <Check size={13} /> : r.status === 'missing' ? <X size={13} /> : <CircleHelp size={13} />}
                    </span>
                    <span><span style={{ color: T.text, fontWeight: 600 }}>{r.label}</span>{r.detail ? <span style={{ color: T.textMuted }}> – {r.detail}</span> : null}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {booking && deciding && (
        <div style={{ padding: '0 16px 14px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 700, color: T.text, flex: '1 1 200px' }}>
            Tid för videointervju
            <Input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} data-testid="interview-time" />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 700, color: T.text, flex: '2 1 240px' }}>
            Meddelande (valfritt)
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="T.ex. ta med jägarexamensbeviset" />
          </label>
          <Button style={btn} loading={busy === 'book'} onClick={book}>Boka och mejla</Button>
          <Button style={btn} variant="ghost" onClick={() => setBooking(false)}>Avbryt</Button>
        </div>
      )}

      <div style={{ padding: '10px 16px 14px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {deciding && <>
          <Button style={btn} loading={busy === 'approve'} onClick={approve} data-testid="admission-approve">Godkänn</Button>
          <Button style={btn} variant="secondary" disabled={!!busy} onClick={reject} data-testid="admission-reject">Avslå</Button>
          {!booking && <Button style={btn} variant="ghost" onClick={() => setBooking(true)}>{a.interviewAt ? 'Ändra intervjutid' : 'Boka intervju'}</Button>}
          <Button style={btn} variant="ghost" loading={busy === 'join'} onClick={join}>Starta videointervju</Button>
        </>}
        {a.stage === 'approved' && <>
          <span style={{ ...TYPE.small, color: T.textSub }}>{free ? 'Godkänd.' : 'Godkänd – kortet är draget.'}</span>
          <Button style={btn} loading={busy === 'send'} onClick={() => run('send', () => api.sendAdmissionContract(a.id))}>Skicka avtal</Button>
        </>}
        {a.stage === 'contract_sent' && a.contract && <>
          <span style={{ ...TYPE.small, color: T.textSub, flex: '1 1 220px' }}>Avtal skickat {fmt(a.contract.sentAt)} till {a.contract.signerEmail} – väntar på signatur.</span>
          <Button style={btn} variant="secondary" loading={busy === 'send'} onClick={() => { if (confirm('Skicka en ny länk? Den gamla slutar gälla.')) void run('send', () => api.sendAdmissionContract(a.id)); }}>Skicka igen</Button>
        </>}
        {a.contract?.status === 'signed' && <>
          <span style={{ ...TYPE.small, color: T.textSub, flex: '1 1 220px' }}>
            Signerat {fmt(a.contract.signedAt)} av {a.contract.signedName}
            {a.stage === 'member' ? ` · i gruppen ${a.groupName ?? ''}${a.validUntil ? ` till ${fmt(a.validUntil, { dateStyle: 'medium' })}` : ''}` : ''}
            {a.stage === 'ended' ? ' · medlemskapet har löpt ut' : ''}
          </span>
          <Button style={btn} variant="secondary" loading={busy === 'pdf'} onClick={() => run('pdf', () => api.openSigningFile('signed', a.contract!.id))}>Signerat avtal (PDF)</Button>
        </>}
        {a.stage === 'signed' && <Button style={btn} loading={busy === 'joinGroup'} onClick={() => run('joinGroup', () => api.retryAdmissionJoin(a.id))}>Lägg i gruppen</Button>}
        {(a.stage === 'expired' || a.stage === 'rejected') && <span style={{ ...TYPE.small, color: T.textMuted }}>{a.stage === 'expired' ? 'Ingen beslutade i tid – reservationen släpptes och inget drogs.' : 'Avslagen – reservationen släpptes och inget drogs.'}</span>}
        {a.lastError && <div role="alert" style={{ ...TYPE.small, color: T.danger, width: '100%' }}>{a.lastError}</div>}
        {err && <div role="alert" style={{ ...TYPE.small, color: T.danger, width: '100%' }}>{err}</div>}
      </div>
    </Card>
  );
}
